import BottomSheet from '@components/BottomSheet'
import ChainLogo, { TokenChainAvatar } from '@components/ChainLogo'
import ChainPicker from '@components/ChainPicker'
import ErrorPopup from '@components/ErrorPopup'
import Picker from '@components/Picker'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { config } from '@plug-core/appConfig'
import useConfig from '@plug-core/hooks/useConfig'
import useEvmChain from '@plug-core/hooks/useEVMChain'
import { Chain, PlugToken } from '@plug-core/interfaces/common'
import { GAEventName } from '@plug-core/interfaces/googleAnalytics'
import { logger } from '@plug-core/logger'
import { useGoogleAnalytics } from '@plug-core/providers/GoogleAnalyticsProvider'
import {
  useCurrentWallet,
  useCustomTokens,
  useUserEVMChains,
  useUserTokens,
} from '@plug-core/state'
import getTokenMetadata from '@plug-core/utils/tokenActions/getMetadata'
import { validateCanisterId } from '@plug-core/utils/validationHelper'
import { Button, Field, Heading, Input, Text } from '@ui'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiChevronDown } from 'react-icons/hi2'

const ICP_TOKEN_STANDARDS = [
  { key: 'ICRC2', humanValue: 'ICRC-2' },
  { key: 'ICRC1', humanValue: 'ICRC-1' },
  { key: 'DIP20', humanValue: 'DIP20' },
  { key: 'DRC20', humanValue: 'DRC20' },
]

const ETH_TOKEN_STANDARDS = [{ key: 'ERC20', humanValue: 'ERC-20' }]
const SOL_TOKEN_STANDARDS = [{ key: 'SPL', humanValue: 'SPL' }]

const getChainStandards = (chain: string) => {
  switch (chain) {
    case Chain.ICP:
      return ICP_TOKEN_STANDARDS
    case Chain.SOL:
      return SOL_TOKEN_STANDARDS
    default:
      return ETH_TOKEN_STANDARDS
  }
}

interface Props {
  hideForm: () => void
  customSaveAction?: (token: PlugToken) => void
}

const CustomTokenForm: FC<Props> = ({ hideForm, customSaveAction }) => {
  const [contractAddress, setContractAddress] = useState('')
  const [standard, setStandard] = useState('')
  const [addError, setAddError] = useState<string>()
  const [metadataLoading, setMetadataLoading] = useState(false)
  const [chainSelectVisible, setChainSelectVisible] = useState(false)
  const [tokenOverviewVisible, setTokenOverviewVisible] = useState(false)
  const [selectedChain, setSelectedChain] = useState<string>(Chain.SOL)
  const [token, setToken] = useState<PlugToken>()

  const { tokens } = useConfig()
  const { currentWallet } = useCurrentWallet()
  const customTokens = useCustomTokens()
  const { toggleToken, isTokenAdded } = useUserTokens()

  const { t } = useTranslation()
  const { fireEvent } = useGoogleAnalytics()
  const { userEVMChains } = useUserEVMChains()

  const evmChain = useEvmChain(selectedChain)

  const validPrincipal = useMemo(() => {
    if (!contractAddress || selectedChain !== Chain.ICP) return true

    return validateCanisterId(contractAddress)
  }, [contractAddress, selectedChain])

  const agent = useMemo(() => {
    return config.getAgent()
  }, [])

  const saveToken = (newToken: PlugToken) => {
    if (customSaveAction) {
      customSaveAction(newToken)
    } else {
      customTokens.addToken(currentWallet, newToken)
      if (!isTokenAdded(currentWallet, newToken.id)) {
        toggleToken(currentWallet, newToken.id)
      }
    }

    fireEvent(GAEventName.CUSTOM_TOKEN_ADDED, {
      standard,
      address: newToken.address!,
      name: newToken.name,
    })

    hideForm()
  }

  const onCustomTokenAdd = useCallback(() => {
    const fetchToken = async () => {
      setMetadataLoading(true)

      try {
        const token = await getTokenMetadata(contractAddress, selectedChain, {
          standard,
          agent,
          evmChain,
        })
        if (token) {
          setToken(token)
          setTokenOverviewVisible(true)
        } else {
          setAddError(t('manageTokens.tokenAddError'))
        }
      } catch (e) {
        setAddError(t('manageTokens.tokenAddError'))
        logger.error(`Could not fetch custom token metadata(${contractAddress})`, {
          originalError: e,
        })
      } finally {
        setMetadataLoading(false)
      }
    }

    const existingToken = tokens.find(
      (token) =>
        token.address?.toLowerCase() === contractAddress.toLowerCase() &&
        token.chain === selectedChain
    )
    if (!existingToken) {
      fetchToken()
    } else {
      hideForm()
      if (!isTokenAdded(currentWallet, existingToken.id)) {
        toggleToken(currentWallet, existingToken.id)
      }
    }
  }, [contractAddress, standard, agent, currentWallet])

  return (
    <>
      <div className="fixed top-0 z-[2]">
        <ErrorPopup
          visible={!!addError}
          title="Error"
          description={addError}
          onDismiss={() => setAddError(undefined)}
        />
      </div>

      <div className="flex flex-col gap-4">
        <Titlebar title={t('manageTokens.addCustom')} />

        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => setChainSelectVisible(true)}
          className="inline-flex cursor-pointer items-center gap-2 self-start rounded-pill border-0 bg-transparent p-1 font-text text-[15px] font-medium text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          <ChainLogo chain={selectedChain} size={25} />
          {evmChain ? evmChain.name : t(`chains.long.${selectedChain}`)}
          <HiChevronDown aria-hidden="true" className="size-5 text-fg-3" />
        </button>

        <div className="flex flex-col gap-2">
          <Field error={validPrincipal === false}>
            <Input
              mono
              aria-label={t('manageTokens.tokenAddress')}
              value={contractAddress}
              onChange={(e) => setContractAddress(e.target.value)}
              placeholder={t('manageTokens.tokenAddress')}
            />
          </Field>
          <Picker
            values={getChainStandards(selectedChain)}
            currentValue={standard}
            onValueChange={setStandard}
            heading={t('manageTokens.selectStandard')}
          />
        </div>
        <Button
          variant="primary"
          disabled={!validPrincipal || !standard}
          fullWidth
          onClick={onCustomTokenAdd}
          loading={metadataLoading}
        >
          {t('manageTokens.addCustom')}
        </Button>
      </div>

      <BottomSheet
        visible={tokenOverviewVisible}
        onBackdropClick={() => setTokenOverviewVisible(false)}
      >
        {token && (
          <div className="flex flex-col items-center gap-4 p-2">
            <TokenChainAvatar logo={token.logo} chains={[token.chain]} size={64} />

            <div className="flex w-full flex-col items-center gap-1">
              <Heading level={4} align="center">
                {token.name}
              </Heading>
              <Text variant="body-s" tone="fg-2" align="center">
                {token.symbol}
              </Text>
              <Button
                variant="secondary"
                className="mt-4"
                onClick={() => saveToken(token)}
                fullWidth
              >
                {t('common.approve')}
              </Button>
            </div>
          </div>
        )}
      </BottomSheet>

      <ChainPicker
        chains={[Chain.ICP, Chain.SOL, ...userEVMChains.map((chainId) => chainId.toString())]}
        initialChain={Chain.SOL}
        chainSelectVisible={chainSelectVisible}
        setChainSelectVisible={setChainSelectVisible}
        onChainChange={setSelectedChain}
      />
    </>
  )
}

export default CustomTokenForm
